const AUTHORIZATION_STORAGE_KEY = "team_authorization";

/**
 * @typedef {number} TeamId
 * @typedef {string} Ability
 * @typedef {{id: number, name: string}} TeamSummary
 * @typedef {{id: number, title: string}} RoleSummary
 * @typedef {{team: TeamSummary, is_current: boolean, is_default: boolean, roles: RoleSummary[], permissions: Ability[]}} AuthorizationMembership
 * @typedef {{currentTeamId: TeamId|null, globalPermissions: Ability[], membershipsByTeamId: Record<string, AuthorizationMembership>, loaded: boolean}} AuthorizationState
 * @typedef {{team_ids: TeamId[], explicit_team: boolean}} CollectionAuthorization
 */

export const emptyAuthorizationState = () => ({
  currentTeamId: null,
  globalPermissions: [],
  membershipsByTeamId: {},
  loaded: false,
  super_admin: false,
});

const asTeamId = (value) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const uniqueStrings = (values) => [
  ...new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => typeof value === "string" ? value : value?.title)
      .filter((value) => typeof value === "string" && value.length > 0)
  ),
];

const contractViolation = (message) => {
  if (process.env.NODE_ENV !== "production") {
    throw new Error(`[authorization] ${message}`);
  }
 
};

export const normalizeAuthorization = (payload) => {
  if (!payload || typeof payload !== "object") {
    return emptyAuthorizationState();
  }

  const membershipsByTeamId = {};

  (Array.isArray(payload.memberships) ? payload.memberships : []).forEach((membership) => {
    const teamId = asTeamId(membership?.team?.id);
    if (teamId === null) return;

    if (membershipsByTeamId[teamId]) {
      contractViolation(`Duplicate membership for team ${teamId}.`);
      return;
    }

    membershipsByTeamId[teamId] = {
      team: {
        id: teamId,
        name: typeof membership.team.name === "string" ? membership.team.name : "",
      },
      is_current: membership.is_current === true,
      is_default: membership.is_default === true,
      roles: (Array.isArray(membership.roles) ? membership.roles : [])
        .map((role) => ({
          id: Number(role?.id),
          title: typeof role?.title === "string" ? role.title : "",
        }))
        .filter((role) => Number.isInteger(role.id) && role.id > 0),
      permissions: uniqueStrings(membership.permissions),
    };
  });

  const currentTeamId = asTeamId(payload.current_team_id);
  if (currentTeamId !== null && !membershipsByTeamId[currentTeamId]) {
    contractViolation(`Current team ${currentTeamId} is missing from memberships.`);
  }

  return {
    currentTeamId,
    globalPermissions: uniqueStrings(payload.global_permissions),
    membershipsByTeamId,
    loaded: true,
    super_admin: payload.super_admin === true || payload.super_admin === 1 || payload.super_admin === "1" || payload.super_admin === "true",
  };
};

export const loadPersistedAuthorization = () => {
  try {
    const value = window.localStorage.getItem(AUTHORIZATION_STORAGE_KEY);
    if (!value) return emptyAuthorizationState();

    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object") return emptyAuthorizationState();

    return {
      currentTeamId: asTeamId(parsed.currentTeamId),
      globalPermissions: uniqueStrings(parsed.globalPermissions),
      membershipsByTeamId: parsed.membershipsByTeamId && typeof parsed.membershipsByTeamId === "object"
        ? parsed.membershipsByTeamId
        : {},
      loaded: parsed.loaded === true,
      super_admin: parsed.super_admin === true,
    };
  } catch (_) {
    return emptyAuthorizationState();
  }
};

export const persistAuthorization = (authorization) => {
  try {
    if (authorization.loaded) {
      window.localStorage.setItem(AUTHORIZATION_STORAGE_KEY, JSON.stringify(authorization));
    } else {
      window.localStorage.removeItem(AUTHORIZATION_STORAGE_KEY);
    }
  } catch (_) {
    // The in-memory store remains authoritative when storage is unavailable.
  }
};

export const teamQuery = (teamIds) => {
  if (Array.isArray(teamIds)) {
    const normalized = teamIds.map(asTeamId).filter(id => id !== null);
    return normalized.length === 0 ? {} : { teams: normalized };
  }
  const normalizedTeamId = asTeamId(teamIds);
  return normalizedTeamId === null ? {} : { teams: [normalizedTeamId] };
};

export const extractEntityTeamIds = (entity) => {
  if (!entity || typeof entity !== "object") {
    return [];
  }

  const collected = [];
  const pushTeamId = (value) => {
    const normalized = asTeamId(value);
    if (normalized !== null) {
      collected.push(normalized);
    }
  };

  if (Array.isArray(entity.team_ids)) {
    entity.team_ids.forEach((teamId) => pushTeamId(teamId));
  }

  if (Array.isArray(entity.teams)) {
    entity.teams.forEach((team) => pushTeamId(team?.id));
  }

  pushTeamId(entity.team_id);
  pushTeamId(entity.team?.id);

  return [...new Set(collected)];
};

export const entityAccessTeamIds = (entity) => extractEntityTeamIds(entity);

const isSuperAdminFlag = (value) => value === true || value === 1 || value === "1" || value === "true";

export const hasGlobalAuthorization = (authorization, ability, { isSuperAdmin = false } = {}) =>
  isSuperAdminFlag(isSuperAdmin)
  || (Array.isArray(authorization?.globalPermissions)
    && authorization.globalPermissions.includes(ability));

export const hasTeamAuthorization = (authorization, ability, teamId, { isSuperAdmin = false } = {}) => {
  // Super admins bypass team context requirements entirely
  if (isSuperAdminFlag(isSuperAdmin)) return true;
  
  const normalizedTeamId = asTeamId(teamId);
  if (normalizedTeamId === null) return false;

  return authorization?.membershipsByTeamId?.[normalizedTeamId]?.permissions?.includes(ability) === true;
};

export const authorizedTeamsForAbility = (authorization, ability, { isSuperAdmin = false } = {}) => {
  if (isSuperAdminFlag(isSuperAdmin)) return null;

  return Object.values(authorization?.membershipsByTeamId || {})
    .filter((membership) => Array.isArray(membership?.permissions)
      && membership.permissions.includes(ability))
    .map((membership) => membership.team.id);
};

/**
 * Get all team IDs where the logged user has a specific permission.
 * Used for the new team-based permission model.
 * @param {AuthorizationState} authorization
 * @param {Ability} ability
 * @returns {TeamId[]} Array of team IDs
 */
export const getTeamsWithPermission = (authorization, ability) => {
  if (!authorization || !authorization.membershipsByTeamId) return [];
  
  const result = Object.values(authorization.membershipsByTeamId)
    .filter(membership => Array.isArray(membership?.permissions) && membership.permissions.includes(ability))
    .map(membership => membership.team.id);
   
  return result;
};

/**
 * Get common team IDs between logged user and target entity.
 * Both user and target entity must be in the same team AND logged user must have the permission.
 * @param {AuthorizationState} authorization
 * @param {Ability} ability
 * @param {TeamId[]} targetTeams - Array of team IDs from target entity
 * @returns {TeamId[]} Array of common team IDs where logged user has the permission
 */
export const getCommonTeamsWithPermission = (authorization, ability, targetTeams) => {
  if (!Array.isArray(targetTeams) || targetTeams.length === 0) { 
    return [];
  }
  
  const userTeamsWithPermission = getTeamsWithPermission(authorization, ability);
  const common = userTeamsWithPermission.filter(teamId => targetTeams.includes(teamId));
   
  return common;
};

/**
 * Check if user can perform an action on a specific entity (user/customer).
 * Used for the new team-based permission model.
 * Requires: logged user has permission in at least one team that entity belongs to.
 * @param {AuthorizationState} authorization
 * @param {Ability} ability
 * @param {TeamId[]} targetTeams - Array of team IDs from target entity
 * @returns {boolean}
 */
export const canPerformActionOnEntity = (authorization, ability, targetTeams) => {
  const commonTeams = getCommonTeamsWithPermission(authorization, ability, targetTeams);
  const canPerform = commonTeams.length > 0; 
  return canPerform;
};

export const parseCollectionAuthorization = (response) => {
  const authorization = response?.data?.meta?.authorization ?? response?.meta?.authorization;
  if (!authorization || typeof authorization !== "object") return null;

  return {
    team_ids: [...new Set((Array.isArray(authorization.team_ids) ? authorization.team_ids : [])
      .map(asTeamId)
      .filter((id) => id !== null))],
    explicit_team: authorization.explicit_team === true,
  };
};

export const teamScopedCacheKey = ({ endpoint, teamId = null, query = {}, actorId }) => {
  let team;
  if (Array.isArray(teamId)) {
    const normalized = teamId.map(asTeamId).filter(id => id !== null).sort((a, b) => a - b);
    team = normalized.length === 0 ? "union" : normalized;
  } else {
    team = asTeamId(teamId) ?? "union";
  }
  return JSON.stringify({
    endpoint,
    team,
    query,
    actorId: Number(actorId) || null,
  });
};

export const parseApiError = (error) => ({
  status: error?.response?.status ?? null,
  code: error?.response?.data?.code ?? null,
  message: error?.response?.data?.message ?? error?.message ?? "An error occurred.",
  fieldErrors: error?.response?.data?.errors ?? {},
  dependencies: error?.response?.data?.dependencies ?? null,
});

export const teamContextRequiredError = () => {
  const error = new Error("A team must be selected for this operation.");
  error.response = {
    status: 409,
    data: {
      code: "team_context_required",
      message: error.message,
    },
  };
  return error;
};

export const createLatestRequestGate = () => {
  let activeKey = null;
  let controller = null;

  return {
    start(key) {
      controller?.abort();
      controller = new AbortController();
      activeKey = key;
      return { key, signal: controller.signal };
    },
    isActive(key) {
      return key === activeKey;
    },
    cancel() {
      controller?.abort();
      controller = null;
      activeKey = null;
    },
  };
};
